/**
 * OffsetDragIndicator — Shows an offset crosshair above the finger during vertex drag.
 * 
 * [MAP v5] SaaS Production-Grade upgrade 2026-07-14
 * 
 * Problem: On mobile, the finger covers the vertex being dragged (fat-finger problem).
 * Solution: Show a floating indicator ~80px above the touch point so the user can
 * see exactly where the vertex will land.
 * 
 * Key design decisions (from Claude deep-dive):
 * - Use touchmove on the map container (not on individual markers) to avoid
 *   Leaflet's internal pointer capture issues on Android Chrome
 * - Use a DOM div overlay (not L.divIcon) for zero-latency updates
 * - Only show during active drag (pm:markerdragstart / pm:markerdragend)
 * - Offset: 80px up from touch point (thumb-friendly)
 */

export class OffsetDragIndicator {
  private container: HTMLElement | null = null;
  private indicator: HTMLDivElement | null = null;
  private isDragging = false;
  private touchMoveHandler: ((e: TouchEvent) => void) | null = null;

  constructor(mapContainer: HTMLElement) {
    this.container = mapContainer;
    this._createIndicator();
    this._bindEvents();
  }

  private _createIndicator(): void {
    const el = document.createElement("div");
    el.className = "offset-drag-indicator";
    el.innerHTML = `
      <div class="offset-drag-crosshair">
        <div class="offset-drag-crosshair__h"></div>
        <div class="offset-drag-crosshair__v"></div>
      </div>
      <div class="offset-drag-line"></div>
    `;
    el.style.display = "none";
    el.style.position = "absolute";
    el.style.pointerEvents = "none";
    el.style.zIndex = "9999";
    el.style.transform = "translate(-50%, -100%)";

    if (this.container) {
      this.container.appendChild(el);
    }
    this.indicator = el;
  }

  private _bindEvents(): void {
    if (!this.container) return;

    this.touchMoveHandler = (e: TouchEvent) => {
      if (!this.isDragging || !this.indicator || !this.container) return;
      if (e.touches.length === 0) return;

      const touch = e.touches[0];
      const rect = this.container.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      // Sprint v77 Bug A Fix: 60px above touch point (matches marker offset, Google Maps standard)
      this.indicator.style.left = `${x}px`;
      this.indicator.style.top = `${y - 60}px`;
      this.indicator.style.display = "block";
    };

    this.container.addEventListener("touchmove", this.touchMoveHandler, { passive: true });
  }

  /** Call when pm:markerdragstart fires */
  startDrag(): void {
    this.isDragging = true;
  }

  /** Call when pm:markerdragend fires */
  endDrag(): void {
    this.isDragging = false;
    if (this.indicator) {
      this.indicator.style.display = "none";
    }
  }

  /** Call when the layer is removed or modal closes */
  destroy(): void {
    this.isDragging = false;
    if (this.touchMoveHandler && this.container) {
      this.container.removeEventListener("touchmove", this.touchMoveHandler);
    }
    if (this.indicator && this.indicator.parentNode) {
      this.indicator.parentNode.removeChild(this.indicator);
    }
    this.indicator = null;
    this.container = null;
    this.touchMoveHandler = null;
  }
}
